import type { StaffTeam, TesseractUserDetail } from '../types/staffManager';

/** Anything with a Tesseract membership window (directory team entry or team member row). */
export interface MembershipLike {
  assigned_at: number | null;
  expires_at: number | null;
}

const DAY_MS = 86_400_000;

/** A membership counts as active until its expiry passes; `null` expiry never expires. */
export function isMembershipActive(membership: MembershipLike, now: number = Date.now()): boolean {
  return membership.expires_at === null || membership.expires_at > now;
}

/** Earliest join date across the ACTIVE memberships (unix ms), or null when unknown. */
export function staffSince(teams: MembershipLike[], now: number = Date.now()): number | null {
  const dates = teams
    .filter((t) => isMembershipActive(t, now))
    .map((t) => t.assigned_at)
    .filter((t): t is number => typeof t === 'number' && Number.isFinite(t) && t > 0);
  return dates.length > 0 ? Math.min(...dates) : null;
}

export function tenureDays(fromMs: number | null, now: number = Date.now()): number | null {
  if (fromMs === null || !Number.isFinite(fromMs)) return null;
  return Math.max(0, Math.floor((now - fromMs) / DAY_MS));
}

/**
 * Compact tenure label: `2y 3mo`, `4mo 12d`, `12d`, `<1d`. Months are 30-day approximations —
 * this is a badge, not payroll.
 */
export function formatTenure(fromMs: number | null, now: number = Date.now()): string {
  const days = tenureDays(fromMs, now);
  if (days === null) return '—';
  if (days < 1) return '<1d';
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  const rest = days - years * 365 - months * 30;
  if (years > 0) return months > 0 ? `${years}y ${months}mo` : `${years}y`;
  if (months > 0) return rest > 0 ? `${months}mo ${rest}d` : `${months}mo`;
  return `${days}d`;
}

export type ExpiryState = 'none' | 'active' | 'expiring' | 'expired';

export interface ExpiryInfo {
  state: ExpiryState;
  /** Whole days until (positive) or since (negative) expiry; null when there is no expiry. */
  days: number | null;
}

/** Classifies a membership expiry; `expiring` means within `expiringWithinDays` (default 14). */
export function membershipExpiryState(expiresAt: number | null, now: number = Date.now(), expiringWithinDays: number = 14): ExpiryInfo {
  if (expiresAt === null || !Number.isFinite(expiresAt)) return { state: 'none', days: null };
  const diff = expiresAt - now;
  const days = diff >= 0 ? Math.ceil(diff / DAY_MS) : -Math.ceil(-diff / DAY_MS);
  if (diff <= 0) return { state: 'expired', days };
  if (days <= expiringWithinDays) return { state: 'expiring', days };
  return { state: 'active', days };
}

export interface EligibilityResult {
  ok: boolean;
  /** Human-readable blockers, in the order Tesseract would report them. */
  reasons: string[];
}

/**
 * Mirrors Tesseract's `assign-staff-to-team` pre-conditions so the UI can block a doomed request
 * with the exact reason: PlayCDU-domain teams need Minecraft AND Discord linked; other domains
 * need a verified email; disabled or banned accounts can never join.
 */
export function teamEligibility(user: TesseractUserDetail | null, team: { domain: string } | null): EligibilityResult {
  if (!user) return { ok: false, reasons: ['Select a user first.'] };
  const reasons: string[] = [];
  if (user.user.disabled) reasons.push('Account is disabled.');
  if (user.banned) reasons.push('Account is banned.');
  const domain = team?.domain ?? 'playcdu';
  if (domain === 'playcdu') {
    if (!user.minecraft) reasons.push('Minecraft account must be linked before joining a PlayCDU staff team.');
    if (!user.discord) reasons.push('Discord account must be linked before joining a PlayCDU staff team.');
  } else if (!user.user.email_verified) {
    reasons.push('Email must be verified before joining this team.');
  }
  return { ok: reasons.length === 0, reasons };
}

/**
 * Groups the member would lose by leaving `teamId`: the team's auto-assigned groups minus any
 * that another team they are still an active member of also grants (Tesseract keeps those).
 */
export function groupsLostOnRemoval(
  teams: StaffTeam[],
  userId: string,
  teamId: string,
  now: number = Date.now(),
): { id: string; name: string; display_name: string }[] {
  const team = teams.find((t) => t.id === teamId);
  if (!team) return [];
  const keptGroupIds = new Set<string>();
  for (const other of teams) {
    if (other.id === teamId) continue;
    const member = other.members.find((m) => m.user_id === userId);
    if (!member || !isMembershipActive(member, now)) continue;
    for (const g of other.groups) keptGroupIds.add(g.id);
  }
  return team.groups.filter((g) => !keptGroupIds.has(g.id));
}

/** Quick-pick expiry presets for the assign / expiry dialogs. */
export const EXPIRY_PRESETS: { id: string; label: string; days: number | null }[] = [
  { id: 'none', label: 'No expiry', days: null },
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: '1y', label: '1 year', days: 365 },
];

/** Unix ms `days` from now at the same time of day, or null for "no expiry". */
export function expiryFromPreset(days: number | null, now: number = Date.now()): number | null {
  return days === null ? null : now + days * DAY_MS;
}

/** `YYYY-MM-DD` for a date input, in local time. */
export function toDateInputValue(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Parses a `YYYY-MM-DD` date input as end-of-day local time (unix ms); null when empty/invalid. */
export function fromDateInputValue(value: string): number | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d, 23, 59, 59, 0);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}
